"use client";

import React from "react";
import { ResponsiveModal, useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { UserSelect } from "@/components/shared/UserSelect";
import { Badge } from "@/components/ui/badge";
import { X, User, FileText } from "lucide-react";
import { toast } from "sonner";
import { EmailSignature } from "@/lib/hooks/api/emails/email-signatures";
import {
  useConnectionShares,
  useShareConnection,
  useUnshareConnection,
} from "@/lib/hooks/api/connection-sharing";

interface ShareSignatureModalProps {
  signature: EmailSignature | null;
  isOpen: boolean;
  onClose: () => void;
  onShare?: (signatureId: string, userId: string) => void;
  onUnshare?: (signatureId: string, userId: string) => void;
}

export const ShareSignatureModal: React.FC<ShareSignatureModalProps> = ({
  signature,
  isOpen,
  onClose,
  onShare,
  onUnshare,
}) => {
  const globalModalStack = useGlobalModalStack();
  // Use connection sharing hooks since signatures are tied to connections
  const {
    data: shares = [],
    isLoading: loadingShares,
    error: sharesError,
  } = useConnectionShares(signature?.connection_id || null, isOpen);

  const shareConnectionMutation = useShareConnection();
  const unshareConnectionMutation = useUnshareConnection();

  // Show error toast if shares loading fails
  React.useEffect(() => {
    if (sharesError) {
      toast.error("Failed to load signature shares");
    }
  }, [sharesError]);

  const handleShare = async (userId: string) => {
    if (!signature) {
      toast.error("No signature selected");
      return;
    }

    // Validate user ID
    if (!userId || userId.trim().length === 0) {
      toast.error("Please select a valid user");
      return;
    }

    // Check if already shared with this user
    const alreadyShared = shares.some((share) => share.shared_with_user_id === userId);
    if (alreadyShared) {
      toast.error("This connection is already shared with this user");
      return;
    }

    // Validate signature has content
    if (!signature.content || signature.content.trim().length === 0) {
      toast.error("Cannot share an empty signature");
      return;
    }

    return new Promise<void>((resolve, reject) => {
      shareConnectionMutation.mutate(
        {
          connectionId: signature.connection_id,
          providerConfigKey: signature.provider_config_key,
          shareWithUserId: userId,
        },
        {
          onSuccess: () => {
            toast.success("Connection shared successfully (signatures included)");
            onShare?.(signature.id, userId);
            resolve();
          },
          onError: (error) => {
            toast.error(`Failed to share connection: ${error.message}`);
            reject(error);
          },
        }
      );
    });
  };

  const handleUnshare = (userId: string) => {
    if (!signature) return;

    unshareConnectionMutation.mutate(
      {
        connectionId: signature.connection_id,
        shareWithUserId: userId,
      },
      {
        onSuccess: () => {
          toast.success("Connection unshared successfully (signatures removed)");
          onUnshare?.(signature.id, userId);
        },
        onError: (error) => {
          toast.error(`Failed to unshare connection: ${error.message}`);
        },
      }
    );
  };

  const getDisplayName = (user: { first_name?: string; last_name?: string; email: string }) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.email;
  };

  // Strip HTML tags for preview
  const getSignaturePreview = (content: string) => {
    const div = document.createElement("div");
    div.innerHTML = content;
    const text = div.textContent || div.innerText || "";
    return text.length > 100 ? `${text.substring(0, 100)}...` : text;
  };

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          globalModalStack.closeModal("share-signature");
        }
      }}
      title="Share Email Connection"
      maxWidth="md"
      side="bottom"
    >
      {signature && (
        <div className="space-y-4">
          {/* Signature Info */}
          <div className="p-3 bg-card-50 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-gray-500" />
              <span className="font-medium">{signature.name}</span>
              {signature.is_default && (
                <Badge variant="secondary" className="text-xs">
                  Default
                </Badge>
              )}
            </div>
            <div className="text-sm text-gray-600 bg-card p-2 rounded border max-h-20 overflow-hidden dark:text-gray-400">
              {getSignaturePreview(signature.content)}
            </div>
            <div className="text-xs text-gray-500 mt-2">Owner: {signature.owner_name}</div>
          </div>

          {/* Explanation */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-950/40 dark:border-blue-800">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> Sharing this email connection will give the user access to
              <strong> all signatures</strong> for this connection, not just this one signature.
            </p>
          </div>

          {/* Share with User */}
          <div>
            <label className="text-sm font-medium mb-2 block">Share connection with user:</label>
            <UserSelect
              onUserSelect={handleShare}
              disabled={shareConnectionMutation.isPending}
              placeholder="Select user to share with..."
              showUnassigned={false}
              showAllUsers={false}
            />
          </div>

          {/* Current Shares */}
          {loadingShares ? (
            <div className="text-center py-4">
              <div className="text-sm text-gray-500">Loading shares...</div>
            </div>
          ) : shares.length > 0 ? (
            <div>
              <label className="text-sm font-medium mb-2 block">
                Connection currently shared with:
              </label>
              <div className="space-y-2">
                {shares.map((share) => (
                  <div
                    key={share.id}
                    className="flex items-center justify-between p-2 bg-card-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{getDisplayName(share.app_users)}</span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleUnshare(share.shared_with_user_id)}
                      disabled={unshareConnectionMutation.isPending}
                      className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/40 dark:hover:text-red-400" title="Close"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="text-sm text-gray-500">
                This email connection is not shared with anyone yet.
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
};
